import { connect } from 'react-redux';
import { CardElement, injectStripe } from 'react-stripe-elements';

import { Button, Combobox, Input, Label, Select, Textarea, Typography } from 'components';
import MultiplesInputs from 'components/Inputs/MultiplesInputs';
import { BLANK_OPTION } from 'constants/blank';
import { BillingInfoInput, CurrencyEnum } from 'gql/graphql';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useBillingAccount } from 'providers/BillingInfoProviders/useBillingAccount';
import { useBillingInfoUpdate } from 'providers/BillingInfoProviders/useBillingInfoUpdate';
import { usePaymentMethodAttach } from 'providers/BillingInfoProviders/usePaymentMethodAttach';
import { useSetupIntentCreate } from 'providers/BillingInfoProviders/useSetupIntentCreate';
import useCountry from 'providers/CountryProviders/useCountry';
import { ChangeEvent, ComponentType, FC, FunctionComponent, useEffect, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { inputPaymentStyle } from 'styles/inputPaymentStyle';
import { Option } from 'types/Option';
import { TGenericError } from 'types/TGenericError';
import { useDebouncedCallback } from 'use-debounce';
import { approveBillingDetails, updateSidebarFlow } from './_actions';
import { SIDEBAR_FLOWS } from './_consts';

const emptyBillingInfo = {
  firstName: '',
  lastName: '',
  country: '',
  city: '',
  postalCode: '',
  address: '',
  currency: ''
};

const isValidBillingInfo = (
  inputs: BillingInfoInput,
  keys: Record<string, string | object>
): boolean => {
  const billingInfoKeys = Object.keys(keys);

  const resultString = billingInfoKeys.every((key: string) => {
    const fieldValue = (inputs as unknown as Record<string, string>)[key];
    return typeof fieldValue === 'string' && (fieldValue as string).length > 0;
  });

  return [
    resultString,
    inputs.business ? !!inputs.businessName?.length : true,
    !!inputs.invoiceEmails?.length && inputs.invoiceEmails.every((email) => !!email?.length)
  ].every(Boolean);
};

interface IPaymentDetails {
  currencies: any;
  updateSidebarFlow: (flow: string) => void;
  approveBillingDetails: () => void;
  stripe?: any;
  updateCreditCard?: (creditCard: any) => void;
}

const PaymentDetails: FC<IPaymentDetails> = ({
  stripe,
  currencies,
  updateSidebarFlow,
  approveBillingDetails,
  updateCreditCard = () => {}
}) => {
  const t = useTranslations();
  const route = useRouter();

  const { data: optionsCountry, isFetched: isFetchedCountry } = useCountry();
  const {
    data: billingAccountPayload,
    refetch,
    isFetched: isFetchedBilling
  } = useBillingAccount({
    staleTime: 0
  });

  const { mutateAsync: setupIntentCreate, isPending } = useSetupIntentCreate({
    onSuccess: (response) => {
      if (response.success)
        setState((oldState: any) => ({
          ...oldState,
          clientSecret: response?.clientSecret,
          loadingBillingInfo: false
        }));
    },
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const { mutateAsync: paymentMethodAttach } = usePaymentMethodAttach({
    onSuccess: () => toaster.success(t('messages.success.payment_attached')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const { mutateAsync: billingInfoUpdate } = useBillingInfoUpdate({
    onSuccess: () => toaster.success(t('messages.success.updated_billing')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const [state, setState] = useState<any>({
    billingInfo: {
      ...emptyBillingInfo,
      business: false,
      businessName: '',
      taxid: '',
      invoiceEmails: [''],
      invoiceCcEmails: [''],
      currency: currencies?.at(0)
    },
    initialBillingInfo: { ...emptyBillingInfo },
    loadingBillingInfo: true,
    complete: false,
    error: null,
    clientSecret: null,
    setupIntent: null,
    savingInfo: false
  });

  const isValid = isValidBillingInfo(state.billingInfo, emptyBillingInfo) && state.complete;

  const debouncedMutation = useDebouncedCallback(setupIntentCreate, 1000);

  const currenciesOptions = [
    BLANK_OPTION,
    ...currencies?.map((currency: string) => ({
      label: currency,
      value: currency as CurrencyEnum
    }))
  ];

  const optionsBusiness = [
    { label: t('billings.for_business'), value: true },
    { label: t('billings.personal_use'), value: false }
  ];

  const updateBillingInfo = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        [e.target.name]: e.target.value
      }
    }));
  };

  const updateCountry = ({ value }: Option) => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        country: value
      }
    }));
  };

  const savePaymentDetails = async () => {
    try {
      const { billingInfo: paymentsBillingInfo } = state;

      setState((oldState: any) => ({ ...oldState, savingInfo: true }));

      const response = await billingInfoUpdate({
        address: paymentsBillingInfo.address,
        business: paymentsBillingInfo.business,
        city: paymentsBillingInfo.city,
        country: paymentsBillingInfo.country,
        currency: paymentsBillingInfo.currency,
        firstName: paymentsBillingInfo.firstName,
        invoiceEmails: paymentsBillingInfo.invoiceEmails,
        invoiceCcEmails:
          !!paymentsBillingInfo.invoiceCcEmails?.length &&
          paymentsBillingInfo.invoiceCcEmails?.at(0) !== ''
            ? paymentsBillingInfo.invoiceCcEmails
            : undefined,
        lastName: paymentsBillingInfo.lastName,
        postalCode: paymentsBillingInfo.postalCode,
        businessName: paymentsBillingInfo.businessName ? paymentsBillingInfo.businessName : '',
        taxid: paymentsBillingInfo.taxid ? paymentsBillingInfo.taxid : ''
      });

      if (response.success) {
        setTimeout(() => {
          refetch();
        }, 1000);
      } else {
        throw new Error('Error in billingInfoUpdate');
      }

      const { error, setupIntent } = await stripe?.handleCardSetup(state?.clientSecret);

      if (error) {
        throw new Error('Error in handleCardSetup');
      }

      const creditCard = await paymentMethodAttach({ setupIntent: setupIntent?.id });
      updateCreditCard(creditCard);
      setState((oldState: any) => ({ ...oldState, savingInfo: true }));

      approveBillingDetails();
      updateSidebarFlow(SIDEBAR_FLOWS.ORDER_DETAILS);
      route.push('/translation/translation-requests');
    } catch (error) {
      setState((oldState: any) => ({ ...oldState, savingInfo: false }));
      notifyException(error);
    }
  };

  const handleCancelOrder = () => updateSidebarFlow(SIDEBAR_FLOWS.ORDER_DETAILS);

  const handleCardElement = ({
    complete,
    error
  }: {
    complete: boolean;
    error: { message: string };
  }) => setState((oldState: any) => ({ ...oldState, complete, error }));

  const handleSelectLegacyCurrency = ({ value }: Option) =>
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        currency: value
      }
    }));

  const handleSelectBusiness = ({ value }: Option) => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        business: value
      }
    }));
  };

  const handleInvoiceEmails = (value: (string | Option)[]) => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        invoiceEmails: value
      }
    }));
  };

  const handleInvoiceCCEmails = (value: (string | Option)[]) => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...state.billingInfo,
        invoiceCcEmails: value
      }
    }));
  };

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setState((oldState: any) => ({
      ...oldState,
      billingInfo: {
        ...oldState.billingInfo,
        currency: currencies?.at(0)
      }
    }));
  }, [currencies]);

  useEffect(() => {
    if (isFetchedCountry && isFetchedBilling)
      setState((oldState: any) => ({
        ...oldState,
        billingInfo: {
          ...oldState.billingInfo,
          ...billingAccountPayload?.billingInfo
          // currency:billingAccountPayload?.billingInfo?.currency currencies?.at(0)
        },
        initialBillingInfo: billingAccountPayload?.billingInfo,
        loadingBillingInfo: false
      }));
  }, [billingAccountPayload?.billingInfo, isFetchedBilling, isFetchedCountry]);

  useEffect(() => {
    debouncedMutation({});
  }, [debouncedMutation]);

  return (
    <div className="ordering_form__sidebar">
      <Typography variant="regular1" className="text-tertiary-100">
        {t('billings.show.billing_info')}
      </Typography>
      <div className="mb-4">
        <Typography variant="light4" className="italic text-quaternary-300">
          {t('ordering_form.please_enter_biling_info')}
        </Typography>
      </div>
      {isFetchedBilling && (
        <>
          {!state.loadingBillingInfo && (
            <>
              <div className="ordering_form__split_inputs mb-2">
                <div className="ordering_form__split_input">
                  <Label name="firstName">{t('billings.show.first_name')}</Label>
                  <Input
                    value={state.billingInfo.firstName || ''}
                    id="firstName"
                    name="firstName"
                    type="text"
                    onChange={updateBillingInfo}
                  />
                </div>
                <div className="ordering_form__split_input">
                  <Label name="lastName">{t('billings.show.last_name')}</Label>
                  <Input
                    value={state.billingInfo.lastName || ''}
                    id="lastName"
                    name="lastName"
                    type="text"
                    onChange={updateBillingInfo}
                  />
                </div>
              </div>

              <Label name="country">{t('billings.show.country')}</Label>
              <Combobox
                options={optionsCountry}
                selectedValue={state.billingInfo.country}
                onChange={updateCountry}
                wideHeight
                noClose
                placeholder={t('billings.show.select_country')}
              />

              <div className="ordering_form__split_inputs mb-2">
                <div className="ordering_form__split_input">
                  <Label name="city">{t('billings.show.city')}</Label>
                  <Input
                    value={state.billingInfo.city || ''}
                    id="city"
                    name="city"
                    type="text"
                    onChange={updateBillingInfo}
                  />
                </div>
                <div className="ordering_form__split_input">
                  <Label name="postalCode">{t('billings.show.postal_code')}</Label>
                  <Input
                    value={state.billingInfo.postalCode || ''}
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    onChange={updateBillingInfo}
                  />
                </div>
              </div>

              <Label name="address">{t('billings.show.street_address')}</Label>
              <Textarea
                value={state.billingInfo.address || ''}
                id="address"
                name="address"
                className="min-h-[80px]"
                onChange={updateBillingInfo}
              />
              <div className="mb-2">
                <Label name="">{t('billings.account_type')}</Label>
                <Select
                  selectedValue={state?.billingInfo?.business}
                  options={optionsBusiness}
                  onChange={handleSelectBusiness}
                />
              </div>
              {state?.billingInfo?.business && (
                <div className="split">
                  <div className="mb-2">
                    <Label name="">{t('billings.show.business_name')}</Label>
                    <Input
                      value={state.billingInfo.businessName || ''}
                      name="businessName"
                      type="text"
                      onChange={updateBillingInfo}
                    />
                  </div>
                  <div className="mb-2">
                    <Label name="">{t('billings.show.tax_id')}</Label>
                    <Input
                      value={state.billingInfo.taxid || ''}
                      name="taxid"
                      type="text"
                      onChange={updateBillingInfo}
                    />
                  </div>
                </div>
              )}
              <div className="mb-2">
                <Label name="">{t('billings.show.invoice_email')}</Label>
                <MultiplesInputs
                  type="email"
                  value={state?.billingInfo?.invoiceEmails}
                  onChange={handleInvoiceEmails}
                  labelButton={t('billings.show.add_another_email')}
                />
              </div>
              <div className="mb-2">
                <Label name="">{t('billings.show.invoice_cc_email')}</Label>
                <MultiplesInputs
                  type="email"
                  value={state?.billingInfo?.invoiceCcEmails}
                  onChange={handleInvoiceCCEmails}
                  labelButton={t('billings.show.add_another_email')}
                />
              </div>
              {!state?.billingInfo?.currencyConfirmedAt && (
                <div className="mb-2">
                  <Label name="">{t('billings.show.currency')}</Label>
                  <Select
                    selectedValue={state?.billingInfo?.currency}
                    options={currenciesOptions}
                    onChange={handleSelectLegacyCurrency}
                  />
                </div>
              )}
            </>
          )}
          {/* Credit card
        ============================================================================== */}
          <Label>{t('billings.show.credit_card')}</Label>
          <div className="input-primary py-[0.60rem]">
            <CardElement style={inputPaymentStyle} onChange={handleCardElement} />
          </div>
          {state.error && (
            <p className="texplanation texplanation--danger mb-4">
              <span className="ticon-warning mr-2" />
              {(state.error as { message: string })?.message}
            </p>
          )}
          <button onClick={savePaymentDetails}></button>
          <div className="mt-4 flex flex-col justify-center gap-4">
            <Button
              label={t('ordering_form.confirm_billing_details')}
              onClick={savePaymentDetails}
              size="large"
              className="flex items-center"
              variant="contained"
              disabled={!isValid || state.savingInfo}
            />
            {state.savingInfo ? null : (
              <div className="mt-4 flex justify-center">
                <Button
                  label={t('ordering_form.cancel')}
                  onClick={handleCancelOrder}
                  size="medium"
                  variant="text"
                  loading={isPending}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const PaymentDetailsWithStripe = injectStripe(
  PaymentDetails as unknown as FunctionComponent
) as ComponentType<IPaymentDetails>;

export default connect(({ staticData: { currencies } }) => ({ currencies }), {
  updateSidebarFlow,
  approveBillingDetails
})(PaymentDetailsWithStripe);
